import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node } from '@tiptap/core';
import { Bold, List } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface DocumentRichTextProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    extensions?: Node[];
}

interface MenuBarProps {
    editor: Editor | null;
    disabled?: boolean;
}

const MenuBar: React.FC<MenuBarProps> = ({ editor, disabled }) => {
    if (!editor) {
        return null;
    }

    const toggleBold = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleBold().run();
    };

    const toggleBulletList = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        editor.chain().focus().toggleBulletList().run();
    };

    return (
        <div className="flex gap-1 p-2 border-b border-input/50">
            <button
                type="button"
                onClick={toggleBold}
                disabled={disabled}
                className={cn(
                    'p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    editor.isActive('bold') && 'bg-muted text-foreground'
                )}
                aria-label="Bold"
            >
                <Bold className="h-4 w-4" />
            </button>
            <button
                type="button"
                onClick={toggleBulletList}
                disabled={disabled}
                className={cn(
                    'p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    editor.isActive('bulletList') && 'bg-muted text-foreground'
                )}
                aria-label="Bullet List"
            >
                <List className="h-4 w-4" />
            </button>
        </div>
    );
};

export interface DocumentRichTextRef {
    editor: Editor | null;
}

export const DocumentRichText = forwardRef<
    DocumentRichTextRef,
    DocumentRichTextProps
>(({ value, onChange, placeholder, disabled, className, extensions = [] }, ref) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Enable all heading levels (default is only 1-3, but we use h5)
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
            }),
            ...extensions,
        ],
        content: value || '',
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'outline-none min-h-[150px] px-3 py-2 text-sm',
            },
        },
        onUpdate({ editor }) {
            onChange(editor.getHTML());
        },
    });

    // Expose editor instance via ref
    useImperativeHandle(ref, () => ({
        editor,
    }));

    // Sync external value changes
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
    }, [value, editor]);

    // Update editable state when disabled changes
    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    return (
        <div
            className={cn(
                'w-full rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                disabled && 'opacity-50 cursor-not-allowed',
                className
            )}
        >
            <MenuBar editor={editor} disabled={disabled} />
            <EditorContent
                editor={editor}
                className={cn(
                    '[&_.ProseMirror]:outline-none',
                    '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-4',
                    '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-4',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:text-muted-foreground',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none'
                )}
            />
        </div>
    );
});

DocumentRichText.displayName = 'DocumentRichText';
